'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Image from 'next/image';

export default function RootPage() {
  const { session, loading } = useAuth();
  const router = useRouter();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (session) {
      router?.replace('/orders-management');
    } else {
      router?.replace('/login-screen');
    }
  }, [loading, session, router]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + Math.random() * 12;
      });
    }, 200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="flex h-screen items-center justify-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1a0533 0%, #3b0764 20%, #7c2d12 50%, #92400e 70%, #78350f 85%, #1a0533 100%)',
      }}
    >
      {/* Radial glow overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(251,191,36,0.18) 0%, rgba(234,88,12,0.10) 40%, transparent 70%)',
        }}
      />
      {/* Floating particles */}
      {[...Array(12)]?.map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full opacity-30"
          style={{
            width: `${6 + (i % 4) * 4}px`,
            height: `${6 + (i % 4) * 4}px`,
            background: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#f97316' : '#fde68a',
            left: `${8 + (i * 7.5) % 84}%`,
            top: `${10 + (i * 11) % 80}%`,
            animation: `floatParticle ${3 + (i % 4)}s ease-in-out infinite`,
            animationDelay: `${(i * 0.4) % 2.5}s`,
          }}
        />
      ))}
      <div className="flex flex-col items-center gap-8 w-72 relative z-10">
        {/* Animated Logo */}
        <div className="flex flex-col items-center gap-3 relative">
          {/* Outer glow ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: '140px',
              height: '140px',
              background: 'radial-gradient(circle, rgba(251,191,36,0.35) 0%, rgba(234,88,12,0.15) 60%, transparent 80%)',
              animation: 'pulseGlow 2s ease-in-out infinite',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          {/* Logo image with pulse + float */}
          <div
            style={{
              animation: 'logoFloat 3s ease-in-out infinite',
              filter: 'drop-shadow(0 0 18px rgba(251,191,36,0.7)) drop-shadow(0 0 36px rgba(234,88,12,0.4))',
            }}
          >
            <Image
              src="/assets/images/app_logo-1779082874216.png"
              alt="App Logo"
              width={100}
              height={100}
              className="object-contain relative z-10"
              priority
            />
          </div>
        </div>

        {/* Slider / Progress Bar Loader */}
        <div className="w-full flex flex-col items-center gap-2">
          <div
            className="w-full h-1.5 rounded-full overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #fbbf24, #f97316, #fde68a)',
                boxShadow: '0 0 8px rgba(251,191,36,0.7)',
              }}
            />
          </div>
          <p
            className="text-xs tracking-widest uppercase"
            style={{ color: 'rgba(253,230,138,0.85)', letterSpacing: '0.2em' }}
          >
            Loading…
          </p>
        </div>
      </div>
      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-10px) scale(1.06); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.25); }
        }
        @keyframes floatParticle {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.25; }
          50% { transform: translateY(-18px) scale(1.2); opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}